import {ICartControllerApi, ICartItem} from '../../types/app.types';
import {CartService} from '../services/CartService';
import {ProductsService} from '../services/ProductsService';
import {BIService} from '../services/BIService';
import {StoreMetaDataService} from '../services/StoreMetaDataService';
import {FedopsInteractions} from '../../components/cart/constants';
import {StyleSettingsService} from '../services/StyleSettingsService';
import {SiteStore} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/site-store/SiteStore';

export class CartStore {
  private readonly biService: BIService;
  private readonly cartService: CartService;
  private readonly productsService: ProductsService;
  private readonly controllerApi: ICartControllerApi;
  private readonly storeMetaDataService: StoreMetaDataService;
  private readonly styleSettingsService: StyleSettingsService;
  private screenReaderMessage: string;

  constructor(
    controllerApi: ICartControllerApi,
    private readonly siteStore: SiteStore,
    {
      biService,
      cartService,
      productsService,
      storeMetaDataService,
      styleSettingsService,
    }: {
      biService: BIService;
      cartService: CartService;
      productsService: ProductsService;
      storeMetaDataService: StoreMetaDataService;
      styleSettingsService: StyleSettingsService;
    }
  ) {
    this.controllerApi = controllerApi;
    this.biService = biService;
    this.cartService = cartService;
    this.productsService = productsService;
    this.storeMetaDataService = storeMetaDataService;
    this.styleSettingsService = styleSettingsService;
  }

  private readonly updateItemQuantity = (...args: Parameters<CartService['updateItemQuantity']>) => {
    const fn = () => this.cartService.updateItemQuantity(...args);
    return this.controllerApi.reportFedopsInteraction(FedopsInteractions.UpdateQuantityInteraction, fn);
  };

  private readonly removeItemFromCart = (item: ICartItem) => {
    const {t} = this.controllerApi;

    this.screenReaderMessage = t('cart.sr_item_was_removed', {item_name: item.product.name});
    const fn = () => this.cartService.removeItemFromCart(item);
    return this.controllerApi.reportFedopsInteraction(FedopsInteractions.RemoveItemInteraction, fn);
  };

  private readonly applyCoupon = async (...args: Parameters<CartService['applyCoupon']>) => {
    this.cartService.clearCouponError();
    await this.controllerApi.updateComponent();

    const fn = () => this.cartService.applyCoupon(...args);
    const fnWithFedops = () =>
      this.controllerApi.reportFedopsInteraction(FedopsInteractions.ApplyCouponInteraction, fn);
    this.controllerApi
      .executeWithLoader(fnWithFedops)
      .then(() => this.biService.applyACouponSf(this.cart))
      .catch(() => this.controllerApi.updateComponent());
  };

  private readonly removeCoupon = (...args: Parameters<CartService['removeCoupon']>) => {
    const fn = () => this.cartService.removeCoupon(...args);
    return this.controllerApi.executeWithLoader(fn);
  };

  private readonly sendToggleCouponBi = () => {
    return this.biService.clickOnApplyPromotionalCodeSf(this.cart);
  };

  private readonly sendAddNoteBi = () => {
    return this.biService.clickOnAddNoteToSellerSf(this.cart);
  };

  private readonly updateBuyerNote = (...args: Parameters<CartService['updateBuyerNote']>) => {
    return this.cartService.updateBuyerNote(...args);
  };

  private get cart() {
    return this.cartService.cart;
  }

  private readonly getProductsManifest = () => {
    return this.productsService.manifest(this.cartService.cart);
  };

  private async isCheckoutRegularFlowEnabled() {
    const [hasRegularFlowPaymentMethods, hasNoPaymentMethods] = await Promise.all([
      this.storeMetaDataService.hasRegularFlowPaymentMethods(),
      this.storeMetaDataService.hasAnyPaymentMethods().then((hasAnyPaymentMethods) => !hasAnyPaymentMethods),
    ]);

    return hasRegularFlowPaymentMethods || hasNoPaymentMethods || this.cartService.isZeroCart;
  }

  private async isCheckoutPayPalFastFlowEnabled() {
    const hasFastFlowPaymentMethods = await this.storeMetaDataService.hasFastFlowPayPalPaymentMethods();

    return (
      hasFastFlowPaymentMethods &&
      !this.cartService.isZeroCart &&
      !(
        this.siteStore.experiments.enabled('specs.stores.ResponsiveCartAndPayPalButtons') &&
        !this.cartService.areAllItemsInStock
      )
    );
  }

  private get isCartValid(): boolean {
    return this.cartService.areAllItemsInStock;
  }

  public async toProps() {
    if (this.cartService.isEmpty) {
      return {
        shouldRenderEmptyState: true,
      };
    }

    return {
      cart: this.cart,
      isDigitalCart: this.cartService.isDigitalCart,
      isCheckoutRegularFlowEnabled: await this.isCheckoutRegularFlowEnabled(),
      isCheckoutPayPalFastFlowEnabled: await this.isCheckoutPayPalFastFlowEnabled(),
      manifest: await this.getProductsManifest(),
      applyCoupon: this.applyCoupon,
      removeCoupon: this.removeCoupon,
      couponError: this.cartService.couponError,
      sendToggleCouponBi: this.sendToggleCouponBi,
      removeItemFromCart: this.removeItemFromCart,
      sendAddNoteBi: this.sendAddNoteBi,
      updateBuyerNote: this.updateBuyerNote,
      updateItemQuantity: this.updateItemQuantity,
      screenReaderMessage: this.screenReaderMessage,
      isCartValid: this.isCartValid,
      shouldShowCoupon: this.siteStore.experiments.enabled('specs.stores.UseShowCouponStyleParam')
        ? this.styleSettingsService.shouldShowCoupon
        : true,
      shouldDisplayPickupInSummary: this.siteStore.experiments.enabled('specs.stores.DisplayPickupInTotalSummary'),
      shouldShowBuyerNote: this.styleSettingsService.shouldShowBuyerNote,
    };
  }
}
